import { c as _c } from "react/compiler-runtime";
import { FaroTable } from "@components/common/faro-table/faro-table";
import { FaroTableColumnVisibilityMenu } from "@components/common/faro-table/faro-table-column-visibility-menu";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { Skeleton } from "@flat-ui";
import { useApplyFilters } from "@hooks/table-filters/use-apply-filters";
import { useMediaQueryList } from "@hooks/use-media-query";
import { useMembers } from "@hooks/use-members";
import { ProjectMarkupsBoard } from "@pages/project-details/project-markups/board/project-markups-board";
import { useMarkupsViewQueryParams } from "@pages/project-details/project-markups/calendar/use-markups-view-query-params";
import { useMarkupsShownInView } from "@pages/project-details/project-markups/calendar/use-markups-shown-in-view";
import { findListedMarkupElement, SELECTED_MARKUP_ROW_SX } from "@pages/project-details/project-markups/markup-selection-styles";
import { useProjectMarkupBulkActions } from "@pages/project-details/project-markups/project-markups-bulk-actions/use-project-markup-bulk-actions";
import { isMarkupsSortField, sortMarkups } from "@pages/project-details/project-markups/markups-toolbar/markups-sort-utils";
import { MarkupsToolbar } from "@pages/project-details/project-markups/markups-toolbar/markups-toolbar";
import { MarkupsToolbarExport } from "@pages/project-details/project-markups/markups-toolbar/markups-toolbar-export";
import { useMarkupsSortQueryParam } from "@pages/project-details/project-markups/markups-toolbar/use-markups-sort-query-param";
import { useProjectMarkupsColumns } from "@pages/project-details/project-markups/project-markups-columns";
import { projectMarkupsFilterChips } from "@pages/project-details/project-markups/project-markups-filters/project-markups-filter-chips";
import { useProjectMarkupsFilterLists } from "@pages/project-details/project-markups/project-markups-filters/project-markups-filter-list";
import { useRemoveUnknownDueDateFilters } from "@pages/project-details/project-markups/project-markups-filters/use-remove-unknown-due-date-filters";
import { getMarkupQuickSearchText, PROJECT_MARKUPS_TABLE_COLUMN_LABELS, PROJECT_MARKUPS_TABLE_COLUMNS, ProjectMarkupsTableHeaders } from "@pages/project-details/project-markups/project-markups-table-utils";
import { ProjectMarkupsMobileView } from "@pages/project-details/project-markups/mobile/project-markups-mobile-view";
import { MarkupSidePanel } from "@pages/project-details/project-markups/sidepanel/markup-sidepanel";
import { useMarkupSidePanelLayout } from "@pages/project-details/project-markups/sidepanel/use-markup-side-panel-layout";
import { useTrackMarkupsLoaded } from "@pages/project-details/project-markups/use-track-markups-loaded";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { resetTableState, setTableType } from "@store/table/table-slice";
import { sidebarSelector } from "@store/ui/ui-selector";
import { updateSidebar } from "@store/ui/ui-slice";
import { currentUserSelector } from "@store/user/user-selector";
import { SidePanelEvents, TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { lazy, Suspense, useEffect, useState } from "react";

// Keeps the scheduler package out of the main bundle until the calendar is shown
const ProjectMarkupsCalendar = lazy(async () => ({
  default: (await import("@pages/project-details/project-markups/calendar/project-markups-calendar")).ProjectMarkupsCalendar
}));
/**
 * List of the Project markups for the currently selected project
 */
export function ProjectMarkupsTable(t0) {
  const $ = _c(97);
  const {
    isLoading,
    projectId
  } = t0;
  const dispatch = useAppDispatch();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    viewMode
  } = useMarkupsViewQueryParams();
  const sidePanelLayout = useMarkupSidePanelLayout();
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const {
    companyMembers,
    projectMembers
  } = useMembers();
  const currentUser = useAppSelector(currentUserSelector);
  const {
    columns,
    onColumnOrderChange,
    forcedVisibleColumns
  } = useProjectMarkupsColumns();
  const {
    markups,
    updateSelectedMarkup,
    selectedMarkup,
    isOpenMarkupSidePanel,
    updateIsSidePanelOpen,
    selectedMarkupId
  } = useMarkupContext();
  const allTableFilters = useProjectMarkupsFilterLists();
  useRemoveUnknownDueDateFilters(allTableFilters[ProjectMarkupsTableHeaders.dueDate]);
  let t1;
  if ($[0] !== companyMembers || $[1] !== currentUser || $[2] !== projectMembers) {
    t1 = (markup, quickFilter) => getMarkupQuickSearchText({
      markup,
      companyMembers,
      projectMembers,
      currentUser,
      quickFilter
    });
    $[0] = companyMembers;
    $[1] = currentUser;
    $[2] = projectMembers;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== allTableFilters || $[5] !== markups || $[6] !== t1) {
    t2 = {
      entities: markups,
      allTableFilters,
      requiredColumns: PROJECT_MARKUPS_TABLE_COLUMNS,
      quickFilterEntity: t1
    };
    $[4] = allTableFilters;
    $[5] = markups;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const filteredMarkups = useApplyFilters(t2);
  const [sortItem, setSortItem] = useMarkupsSortQueryParam();
  let t3;
  if ($[8] !== companyMembers || $[9] !== filteredMarkups || $[10] !== projectMembers || $[11] !== sortItem) {
    t3 = sortMarkups(filteredMarkups, sortItem, {
      companyMembers,
      projectMembers
    });
    $[8] = companyMembers;
    $[9] = filteredMarkups;
    $[10] = projectMembers;
    $[11] = sortItem;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const sortedMarkups = t3;
  let t4;
  if ($[13] !== projectId || $[14] !== sortedMarkups) {
    t4 = {
      markups: sortedMarkups,
      projectId
    };
    $[13] = projectId;
    $[14] = sortedMarkups;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const bulkActionButtons = useProjectMarkupBulkActions(t4);
  const [sortedTableRowIds, setSortedTableRowIds] = useState();
  const shownMarkups = useMarkupsShownInView(sortedMarkups, isScreenSmOrSmaller ? undefined : sortedTableRowIds);
  let t5;
  if ($[16] !== filteredMarkups.length || $[17] !== isLoading || $[18] !== markups.length) {
    t5 = {
      isLoading,
      totalCount: markups.length,
      shownCount: filteredMarkups.length
    };
    $[16] = filteredMarkups.length;
    $[17] = isLoading;
    $[18] = markups.length;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  useTrackMarkupsLoaded(t5);
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = projectMarkupsFilterChips();
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const filterChips = t6;
  const {
    isFullSize
  } = useAppSelector(sidebarSelector);
  let t7;
  let t8;
  if ($[21] !== markups || $[22] !== selectedMarkup || $[23] !== updateSelectedMarkup) {
    t7 = () => {
      if (selectedMarkup) {
        const updatedMarkup = markups.find(markup_0 => markup_0.id === selectedMarkup.id);
        if (updatedMarkup) {
          updateSelectedMarkup(updatedMarkup);
        }
      }
    };
    t8 = [selectedMarkup, markups, updateSelectedMarkup];
    $[21] = markups;
    $[22] = selectedMarkup;
    $[23] = updateSelectedMarkup;
    $[24] = t7;
    $[25] = t8;
  } else {
    t7 = $[24];
    t8 = $[25];
  }
  useEffect(t7, t8);
  const selectedId = selectedMarkup?.id;
  let t10;
  let t9;
  if ($[26] !== selectedId) {
    t9 = () => {
      if (selectedId) {
        findListedMarkupElement(selectedId)?.scrollIntoView({
          block: "nearest",
          behavior: "smooth"
        });
      }
    };
    t10 = [selectedId];
    $[26] = selectedId;
    $[27] = t10;
    $[28] = t9;
  } else {
    t10 = $[27];
    t9 = $[28];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[29] !== dispatch) {
    t11 = () => {
      dispatch(setTableType(TableType.projectMarkups));
      return () => {
        dispatch(resetTableState());
      };
    };
    t12 = [dispatch];
    $[29] = dispatch;
    $[30] = t11;
    $[31] = t12;
  } else {
    t11 = $[30];
    t12 = $[31];
  }
  useEffect(t11, t12);
  let t13;
  if ($[32] !== dispatch || $[33] !== isFullSize || $[34] !== isOpenMarkupSidePanel || $[35] !== markups || $[36] !== selectedMarkup?.id || $[37] !== trackEvent || $[38] !== updateIsSidePanelOpen || $[39] !== updateSelectedMarkup || $[40] !== viewMode) {
    t13 = function onRowClicked(clickedMarkup) {
      if (isFullSize) {
        dispatch(updateSidebar({
          isFullSize: false
        }));
      }
      trackEvent({
        name: SidePanelEvents.openSidePanelMarkups,
        props: {
          viewMode
        }
      });
      const markup_2 = markups.find(markup_1 => markup_1.id === clickedMarkup.id);
      if (markup_2) {
        const isSameMarkup = markup_2.id === selectedMarkup?.id;
        updateSelectedMarkup(markup_2);
        updateIsSidePanelOpen(!isOpenMarkupSidePanel || !isSameMarkup);
      }
    };
    $[32] = dispatch;
    $[33] = isFullSize;
    $[34] = isOpenMarkupSidePanel;
    $[35] = markups;
    $[36] = selectedMarkup?.id;
    $[37] = trackEvent;
    $[38] = updateIsSidePanelOpen;
    $[39] = updateSelectedMarkup;
    $[40] = viewMode;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  const onRowClicked = t13;
  let content;
  if (viewMode === "calendar") {
    let t14;
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <Skeleton variant="rectangular" sx={{
        width: "100%",
        height: "36rem"
      }} />;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    let t15;
    if ($[43] !== isLoading || $[44] !== onRowClicked || $[45] !== sortedMarkups) {
      t15 = <Suspense fallback={t14}><ProjectMarkupsCalendar markups={sortedMarkups} isLoading={isLoading} onMarkupClick={onRowClicked} /></Suspense>;
      $[43] = isLoading;
      $[44] = onRowClicked;
      $[45] = sortedMarkups;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    content = t15;
  } else {
    if (viewMode === "board") {
      let t14;
      if ($[47] !== isLoading || $[48] !== onRowClicked || $[49] !== sortedMarkups) {
        t14 = <ProjectMarkupsBoard markups={sortedMarkups} isLoading={isLoading} onMarkupClick={onRowClicked} />;
        $[47] = isLoading;
        $[48] = onRowClicked;
        $[49] = sortedMarkups;
        $[50] = t14;
      } else {
        t14 = $[50];
      }
      content = t14;
    } else {
      if (isScreenSmOrSmaller) {
        const t14 = `${sortItem.field}-${sortItem.sort}`;
        let t15;
        if ($[51] !== bulkActionButtons || $[52] !== isLoading || $[53] !== onRowClicked || $[54] !== sortedMarkups || $[55] !== t14) {
          t15 = <ProjectMarkupsMobileView markups={sortedMarkups} isLoading={isLoading} sortKey={t14} onMarkupClick={onRowClicked} bulkActionButtons={bulkActionButtons} />;
          $[51] = bulkActionButtons;
          $[52] = isLoading;
          $[53] = onRowClicked;
          $[54] = sortedMarkups;
          $[55] = t14;
          $[56] = t15;
        } else {
          t15 = $[56];
        }
        content = t15;
      } else {
        let t14;
        if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
          t14 = [ProjectMarkupsTableHeaders.name];
          $[57] = t14;
        } else {
          t14 = $[57];
        }
        let t15;
        if ($[58] !== forcedVisibleColumns) {
          t15 = <FaroTableColumnVisibilityMenu labelsMap={PROJECT_MARKUPS_TABLE_COLUMN_LABELS} disabledColumns={t14} forcedVisibleColumns={forcedVisibleColumns} tableType={TableType.projectMarkups} />;
          $[58] = forcedVisibleColumns;
          $[59] = t15;
        } else {
          t15 = $[59];
        }
        let t16;
        if ($[60] !== sortItem) {
          t16 = [sortItem];
          $[60] = sortItem;
          $[61] = t16;
        } else {
          t16 = $[61];
        }
        let t17;
        if ($[62] !== setSortItem) {
          t17 = t18 => {
            const [newSortItem] = t18;
            if (newSortItem?.sort && isMarkupsSortField(newSortItem.field)) {
              setSortItem({
                field: newSortItem.field,
                sort: newSortItem.sort
              });
            }
          };
          $[62] = setSortItem;
          $[63] = t17;
        } else {
          t17 = $[63];
        }
        let t18;
        if ($[64] !== selectedMarkup?.id || $[65] !== selectedMarkupId) {
          t18 = rowData => rowData.id === selectedMarkupId || selectedMarkup?.id === rowData.id ? "markups-selected-side-panel" : "";
          $[64] = selectedMarkup?.id;
          $[65] = selectedMarkupId;
          $[66] = t18;
        } else {
          t18 = $[66];
        }
        let t19;
        if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
          t19 = {
            cursor: "pointer"
          };
          $[67] = t19;
        } else {
          t19 = $[67];
        }
        let t20;
        if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
          t20 = {
            minHeight: "25rem",
            "& .MuiDataGrid-row": t19,
            "& .markups-selected-side-panel": SELECTED_MARKUP_ROW_SX,
            "& .MuiDataGrid-cell": {
              cursor: "pointer",
              padding: "0"
            },
            "& .MuiDataGrid-columnHeader:hover .drag-handle": {
              display: "flex"
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              width: "100%"
            }
          };
          $[68] = t20;
        } else {
          t20 = $[68];
        }
        let t21;
        if ($[69] !== bulkActionButtons || $[70] !== columns || $[71] !== isLoading || $[72] !== onColumnOrderChange || $[73] !== onRowClicked || $[74] !== sortedMarkups || $[75] !== t15 || $[76] !== t16 || $[77] !== t17 || $[78] !== t18) {
          t21 = <FaroTable entities={sortedMarkups} uniqueIdKey="id" isLoading={isLoading} columns={columns} rowHeight={54} onRowClick={onRowClicked} bulkActionButtons={bulkActionButtons} tableType={TableType.projectMarkups} filterMenuTrigger={t15} onColumnOrderChange={onColumnOrderChange} sortingMode="server" sortModel={t16} onSortModelChange={t17} onSortedRowIdsChange={setSortedTableRowIds} rowClassName={t18} sx={t20} />;
          $[69] = bulkActionButtons;
          $[70] = columns;
          $[71] = isLoading;
          $[72] = onColumnOrderChange;
          $[73] = onRowClicked;
          $[74] = sortedMarkups;
          $[75] = t15;
          $[76] = t16;
          $[77] = t17;
          $[78] = t18;
          $[79] = t21;
        } else {
          t21 = $[79];
        }
        content = t21;
      }
    }
  }
  let t14;
  if ($[80] !== projectId || $[81] !== sortedMarkups) {
    t14 = <MarkupsToolbarExport projectId={projectId} markups={sortedMarkups} />;
    $[80] = projectId;
    $[81] = sortedMarkups;
    $[82] = t14;
  } else {
    t14 = $[82];
  }
  let t15;
  if ($[83] !== isLoading || $[84] !== markups.length || $[85] !== setSortItem || $[86] !== sortItem || $[87] !== sortedMarkups.length || $[88] !== t14) {
    t15 = <MarkupsToolbar filterChips={filterChips} sortItem={sortItem} onSortItemChange={setSortItem} shownCount={sortedMarkups.length} totalCount={markups.length} isLoading={isLoading} exportComponent={t14} />;
    $[83] = isLoading;
    $[84] = markups.length;
    $[85] = setSortItem;
    $[86] = sortItem;
    $[87] = sortedMarkups.length;
    $[88] = t14;
    $[89] = t15;
  } else {
    t15 = $[89];
  }
  let t16;
  if ($[90] !== shownMarkups || $[91] !== sidePanelLayout) {
    t16 = <MarkupSidePanel layout={sidePanelLayout} markups={shownMarkups} />;
    $[90] = shownMarkups;
    $[91] = sidePanelLayout;
    $[92] = t16;
  } else {
    t16 = $[92];
  }
  let t17;
  if ($[93] !== content || $[94] !== t15 || $[95] !== t16) {
    t17 = <>{t15}{content}{t16}</>;
    $[93] = content;
    $[94] = t15;
    $[95] = t16;
    $[96] = t17;
  } else {
    t17 = $[96];
  }
  return t17;
}